import { useEffect, useState } from 'react'
import style from '../Geral/geral.module.css'
import BackButton from '../../layout/BackButton'
import Button from '../../layout/Button/Button'
import { useAuth } from '../../../hooks/AuthContext'
import { useToast } from '../../../hooks/Toast'
import {
  gerarConvite,
  revogarConvite,
  buscarConvitesAtivos,
  buscarEscolasVinculadas,
  buscarMinhaHierarquia,
} from '../../../firebase/hierarquia'

// URL base do front — em produção isso deveria vir de uma env var (igual
// FRONTEND_URL no backend), mas como aqui é só pra montar um link exibido
// na tela (não afeta segurança nenhuma — quem valida é a Cloud Function),
// usar window.location.origin resolve sem precisar de configuração nova.
function montarLinkConvite(token) {
  return `${window.location.origin}/#/vincular/${token}`
}

export default function Hierarquia() {
  const { usuario } = useAuth()
  const { mostrarAviso } = useToast()

  const [minhaHierarquia, setMinhaHierarquia] = useState(null)
  const [convites, setConvites] = useState([])
  const [vinculadas, setVinculadas] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [gerando, setGerando] = useState(null) // 'regional' | 'geral' | null

  const carregarTudo = async () => {
    setCarregando(true)
    try {
      const [hierarquia, listaConvites, listaVinculadas] = await Promise.all([
        buscarMinhaHierarquia(usuario.idEscola),
        buscarConvitesAtivos(usuario.idEscola),
        buscarEscolasVinculadas(usuario.idEscola),
      ])
      setMinhaHierarquia(hierarquia)
      setConvites(listaConvites)
      setVinculadas(listaVinculadas)
    } catch (error) {
      mostrarAviso('Erro ao carregar dados de hierarquia: ' + error.message, 'erro')
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => {
    if (usuario?.idEscola) carregarTudo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.idEscola])

  const handleGerarConvite = async (tipoAlvo) => {
    setGerando(tipoAlvo)
    try {
      await gerarConvite(usuario.idEscola, tipoAlvo)
      await carregarTudo()
    } catch (error) {
      mostrarAviso('Erro ao gerar convite: ' + error.message, 'erro')
    } finally {
      setGerando(null)
    }
  }

  const handleRevogar = async (token) => {
    if (!window.confirm('Revogar este convite? O link deixa de funcionar (vínculos já feitos continuam valendo).')) return
    try {
      await revogarConvite(usuario.idEscola, token)
      mostrarAviso('Convite revogado', 'sucesso')
      await carregarTudo()
    } catch (error) {
      mostrarAviso('Erro ao revogar convite: ' + error.message, 'erro')
    }
  }

  const copiarLink = (token) => {
    navigator.clipboard?.writeText(montarLinkConvite(token))
    mostrarAviso('Link copiado', 'sucesso')
  }

  if (carregando) return <p>Carregando...</p>

  const convitePorTipo = (tipo) => convites.find((c) => c.tipoAlvo === tipo)
  const vinculadasPorTipo = (tipo) => vinculadas.filter((v) => v.tipo === tipo)

  return (
    <section className={`${style.container}`}>
      <BackButton />
      <h3>Hierarquia entre escolas</h3>

      <section>
        <h2>Minha escola está vinculada a</h2>
        <p>
          <strong>Regional: </strong>
          {minhaHierarquia?.nomeRegional || 'nenhuma ainda'}
        </p>
        <p>
          <strong>Geral: </strong>
          {minhaHierarquia?.nomeGeral || 'nenhuma ainda'}
        </p>
        <p style={{ fontSize: 14, color: '#666' }}>
          Isso é feito abrindo o link de convite que a Regional/Geral te enviar (não por aqui).
        </p>
      </section>

      {['regional', 'geral'].map((tipo) => (
        <section key={tipo} style={{ marginTop: 24 }}>
          <h2>Convidar escolas como sua {tipo === 'regional' ? 'Regional' : 'Geral'}</h2>
          <p style={{ fontSize: 14, color: '#666' }}>
            {tipo === 'regional'
              ? 'Gere um link e envie pras escolas que devem passar a te enviar relatório como Regional delas.'
              : 'Gere um link e envie pras escolas (ou Regionais) que devem passar a te enviar relatório como Geral delas.'}
          </p>

          {convitePorTipo(tipo) ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                readOnly
                value={montarLinkConvite(convitePorTipo(tipo).id)}
                style={{ flex: 1, minWidth: 240, padding: 6 }}
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={() => copiarLink(convitePorTipo(tipo).id)}>Copiar link</Button>
              <Button variant="secondary" onClick={() => handleRevogar(convitePorTipo(tipo).id)}>Revogar</Button>
            </div>
          ) : (
            <Button onClick={() => handleGerarConvite(tipo)} disabled={gerando === tipo}>
              {gerando === tipo ? 'Gerando...' : `Gerar link de convite (${tipo})`}
            </Button>
          )}

          <h4 style={{ marginTop: 16 }}>
            Escolas vinculadas como {tipo === 'regional' ? 'suas dependentes (você é a Regional delas)' : 'suas dependentes (você é a Geral delas)'}
          </h4>
          {vinculadasPorTipo(tipo).length === 0 && <p>Nenhuma escola vinculada ainda.</p>}
          <ul>
            {vinculadasPorTipo(tipo).map((v) => (
              <li key={v.id}>{v.nomeEscolaFilha}</li>
            ))}
          </ul>
        </section>
      ))}
    </section>
  )
}
